<template>
  <div class="users">
    <div
      id="user-table"
      class="user-topbar"
    >
      <div class="user-actionbar">
        <div class="searchbar-container">
          <span
            class="searchbar-icon"
            v-html="searchIcon"
          />
          <input
            id="search-input"
            v-model.lazy="searchTerm"
            class="searchbar"
            placeholder="Search Users..."
          />
          <span
            v-show="searchTerm"
            id="clear-search"
            v-tooltip="{ content: 'Clear Search', placement: 'right' }"
            class="searchbar-clear"
            @click="clearSearch"
            v-html="clearIcon"
          />
        </div>
        <transition name="pop">
          <div
            v-show="selectedRows.length > 0"
            class="user-actionbar-container"
          >
            <div class="user-actionbar__selection">
              {{ totalSelected }} User{{ totalSelected > 1 ? "s" : "" }} Selected
              <button
                v-show="totalSelected > 0"
                id="clear-button"
                class="btn btn--grey ml-1 mr-2"
                @click="clearSelectedItems"
              >
                Clear
              </button>
            </div>
            <template v-if="!isModalOpen">
              <div
                id="manage-user-dropdown"
                class="dropdown"
              >
                <button
                  id="manage-user-dropdown-button"
                  class="dropdown-button"
                >
                  Manage
                  <span
                    class="dropdown-button-chevron"
                    v-html="chevronDown"
                  />
                </button>
                <div class="dropdown-content">
                  <template v-if="isBcidAvailable">
                    <div
                      id="enroll-bcid-dropdown"
                      class="dropdown-item"
                      @click="emitEnrollBCID()"
                    >
                      Activate BioConnect Mobile
                    </div>
                    <div class="dropdown__divider" />
                  </template>
                  <template v-if="hasActiveAuthenticator">
                    <div
                      id="test-2fa-dropdown"
                      class="dropdown-item"
                      @click="emitTestStepup()"
                    >
                      Send Test Request
                    </div>
                    <div class="dropdown__divider" />
                  </template>
                  <div
                    id="enable-stepup-dropdown"
                    class="dropdown-item"
                    @click="emitToggleUser(false)"
                  >
                    Enable Mobile 2FA
                  </div>
                  <div
                    id="disable-stepup-dropdown"
                    class="dropdown-item"
                    @click="emitToggleUser(true)"
                  >
                    Disable Mobile 2FA
                  </div>
                  <div
                    v-show="canEditUserDetails"
                    id="add-card-dropdown"
                    class="dropdown-item"
                    @click="emitManageCards()"
                  >
                    Add Credential
                  </div>
                  <div
                    v-show="canEditUserDetails"
                    id="manage-credentials-dropdown"
                    class="dropdown-item"
                    @click="emitManageCredentials()"
                  >
                    Delete Credentials
                  </div>
                  <div class="dropdown__divider" />
                  <div
                    v-show="canEditUserDetails"
                    id="edit-details-dropdown"
                    class="dropdown-item"
                    @click="emitEditUser()"
                  >
                    Edit Details
                  </div>
                </div>
              </div>
            </template>
          </div>
        </transition>
      </div>
      <pagination
        :loading="loading"
        :page.sync="userPaginationParams.page"
        :per-page.sync="userPaginationParams.perPage"
        :total-rows.sync="totalRecords"
        :rows-per-page-label="'Users per page'"
      />
      <div class="user-item user-item__header">
        <input
          v-model="selectAll"
          type="checkbox"
        />
        <span>Name</span>
        <span>Username</span>
        <span>Credentials</span>
        <span class="user-item__header--center">Mobile Verification</span>
      </div>
    </div>
    <template v-if="rows.length > 0">
      <div class="user-table">
        <template v-for="(user, index) in formattedUserRow">
          <label
            :key="index"
            :for="index"
            :data-testid="user.username"
            :class="[{ 'user-item--selected': isSelected(user) }, 'user-item']"
          >
            <input
              :id="index"
              v-model="selectedRows"
              type="checkbox"
              :value="user"
            />
            <span
              class="user-admin"
              :title="user.name"
              data-testid="user-name"
            >
              <user-icon :roles="user.roles" />
              <span>
                {{ user.name }}
              </span>
            </span>
            <span
              :title="user.sanitizedUsername"
              data-testid="user-username"
            >
              {{ user.sanitizedUsername }}
            </span>
            <span class="user-accessMethod">
              <span
                v-tooltip="{ content: cardTooltipText(user) }"
                class="accessMethodIcon"
                data-testid="cardIcon"
                :class="{ disabled: !isCardAdded(user) }"
              >
                <card-icon :size="28" />
              </span>
              <span
                v-tooltip="{ content: pinTooltipText(user) }"
                class="accessMethodIcon"
                data-testid="pinIcon"
                :class="{ disabled: !isPinAdded(user) }"
              >
                <pin-icon :size="28" />
              </span>
              <span
                v-tooltip="{ content: fingerTooltipText(user) }"
                class="accessMethodIcon"
                data-testid="fingerprintIcon"
                :class="{ disabled: !isFingerDataAdded(user) }"
              >
                <biometric-icon :size="28" />
              </span>
            </span>
            <span class="user-verification">
              <template v-if="user.twoFactorAuthEnabled">
                <span
                  v-tooltip="{
                    content: 'Enabled for Two-Factor Authentication using Mobile Verification',
                  }"
                  class="user-verification__enabled"
                >
                  Enabled
                </span>
              </template>
              <template v-else>
                <span
                  v-tooltip="{
                    content: 'Two-Factor Authentication with Mobile Verification is Disabled',
                  }"
                  class="user-verification__disabled"
                >
                  Disabled
                </span>
              </template>
            </span>
          </label>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="empty-state mt-2">
        <template v-if="loading">
          Loading users...
        </template>
        <template v-else>
          <strong>No users could be found.</strong>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import Pagination from '@/components/pagination.vue'
import feather from 'feather-icons'
import UserIcon from './userIcon'
import BiometricIcon from '@/components/icons/biometricIcon.vue'
import PinIcon from '@/components/icons/pinIcon.vue'
import CardIcon from '@/components/icons/cardIcon.vue'

export default {
  name: 'UserTable',
  components: {
    Pagination,
    UserIcon,
    BiometricIcon,
    PinIcon,
    CardIcon,
  },
  props: [
    'rows',
    'totalRecords',
    'loading',
    'isModalOpen',
    'activeAuthenticator',
    'userPaginationParams',
    'search',
    'credentials',
  ],
  data() {
    return {
      selectedRows: [],
      adminIcon: feather.icons.shield.toSvg({width: '1em'}),
      groupAdminIcon: feather.icons.users.toSvg({width: '1em'}),
      chevronDown: feather.icons['chevron-down'].toSvg(),
      searchIcon: feather.icons.search.toSvg(),
      clearIcon: feather.icons.x.toSvg({width: '1em'}),
      mobileIcon: feather.icons.smartphone.toSvg(),
    }
  },
  computed: {
    searchTerm: {
      get() {
        return this.search
      },
      set(value) {
        this.$emit('update:search', value)
      },
    },
    formattedUserRow() {
      const _users = this.rows
      const _blankMessage = 'Unavailable in ACM'
      const _acmID = 'ACMSyncService.com'
      const _formattedUsers = _users.map((user) => {
        const {username, email, firstName, lastName, phoneNumber, cards} = user
        return {
          ...user,
          name: `${firstName} ${lastName}`,
          phoneNumber: phoneNumber === 'null' ? '' : phoneNumber,
          sanitizedUsername: username.includes(_acmID) ? _blankMessage : username,
          sanitizedEmail: email.includes(_acmID) ? _blankMessage : email,
          twoFactorAuthEnabled: this.twoFactorAuthFromCards(cards),
        }
      })
      return _formattedUsers
    },
    canEditUserDetails() {
      if (this.totalSelected === 1) {
        if (this.selectedRows[0].roles[0].name === 'administrator') {
          return this.$can('update', 'user-admins')
        }
        return true
      }
      return false
    },
    selectAll: {
      get: function () {
        return this.formattedUserRow.length === this.selectedRows.length
      },
      set: function (value) {
        const _selected = []
        if (value) {
          this.formattedUserRow.forEach((user) => {
            _selected.push(user)
          })
        }
        this.selectedRows = _selected
      },
    },
    totalSelected() {
      return this.selectedRows.length
    },
    isBcidAvailable() {
      return this.credentials.some((credential) => {
        return credential.type === 'bcid'
      })
    },
    hasActiveAuthenticator() {
      return this.activeAuthenticator !== 'none'
    },
  },
  watch: {
    rows() {
      this.clearSelectedItems()
    },
  },
  methods: {
    twoFactorAuthFromCards(cards) {
      return cards.some((card) => {
        return !card.isBypass
      })
    },
    isSelected(item) {
      return this.selectedRows.includes(item)
    },
    clearSearch() {
      this.searchTerm = ''
    },
    emitToggleUser(isBypass) {
      const _payload = {
        isBypass: isBypass,
        users: this.selectedRows,
      }
      this.$emit('toggle-user', _payload)
    },
    emitManageCards() {
      this.$emit('manage-cards', this.selectedRows)
    },
    emitManageCredentials() {
      this.$emit('manage-credentials', this.selectedRows)
    },
    emitTestStepup() {
      const _usernames = this.selectedRows.map((user) => {
        return user.username
      })
      this.$emit('send-stepup', _usernames)
    },
    emitEditUser() {
      this.$emit('edit-user', this.selectedRows)
    },
    emitEnrollBCID() {
      this.$emit('enroll-mobile', this.selectedRows)
    },
    clearSelectedItems() {
      this.selectedRows.splice(0, this.selectedRows.length)
    },
    isCardAdded(user) {
      return user.cards.length > 0
    },
    isPinAdded(user) {
      return user.hasPin
    },
    isFingerDataAdded(user) {
      return user.hasFingerData
    },
    cardTooltipText(user) {
      return 'Card/User ID ' + (this.isCardAdded(user) ? 'added' : '')
    },
    pinTooltipText(user) {
      return 'PIN ' + (this.isPinAdded(user) ? 'added' : '')
    },
    fingerTooltipText(user) {
      return 'Fingerprint ' + (this.isFingerDataAdded(user) ? 'enrolled' : ' not enrolled')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_tables.scss";

.user {
  &-table {
    width: 100%;
    @media #{$mobile} {
      overflow-x: auto;
    }
  }
  &-item {
    @include grid-table-item();
    grid-template-columns: 0.3fr 2fr 3fr 3fr 2fr;
    &__header {
      @include grid-table-header();
      &--center {
        @include flex-display();
        @include flex-justify-content(center);
        width: 100%;
      }
    }
    @media #{$mobile} {
      width: 1000px;
    }
  }
  &-topbar {
    @include flex-display();
    @include flex-direction(column);
    position: sticky;
    top: 0;
    padding-top: 1em;
    background: $white;
  }
  &-actionbar {
    @include flex-display();
    @include flex-align-items(center);
    @include flex-justify-content(space-between);
    font-size: $small-font;
    &-container {
      @include flex-display();
      @include flex-align-items(center);
    }
    &__selection {
      color: $black;
      font-style: italic;
    }
  }
  &-admin {
    @include flex-display();
    @include flex-align-items(center);
    width: 100%;
    &-icon {
      margin-right: 5px;
      max-height: 1.5em;
      color: $dark-grey;
    }
  }
  &-accessMethod {
    margin: -1.5ch;
  }
  &-verification {
    font-size: $small-font;
    font-weight: 600;
    text-transform: uppercase;
    @include flex-display();
    @include flex-justify-content(center);
    width: 100%;
    &__enabled {
      color: $green;
    }
  }
}

.accessMethodIcon {
  margin: 0 10px;

  &.disabled {
    color: $light-grey;
  }
}
</style>
